import { getPdfDownloadUrl } from 'services/webdashboardReviews';

import { errorReactToastify, infoReactToastify } from './toasts';

/**
 * Custom padStart implementation to avoid using deprecated String.padStart
 * @param {string|number} str - The string or number to pad
 * @param {number} targetLength - The target length
 * @param {string} padString - The string to pad with (default '0')
 * @returns {string} Padded string
 */
const padStart = (str, targetLength = 2, padString = '0') => {
  const strValue = String(str);

  if (strValue.length >= targetLength) {
    return strValue;
  }
  const padLength = targetLength - strValue.length;
  const pad = padString.repeat(Math.ceil(padLength / padString.length));

  return pad.slice(0, padLength) + strValue;
};

/**
 * Generate a sanitized PDF filename with title and datetime
 * @param {string} title - The review title
 * @param {Date} date - Optional date, defaults to current date
 * @returns {string} Sanitized filename in format: title_YYYY-MM-DD_HH_MM.pdf
 */
export const generatePdfFilename = (title, date = new Date()) => {
  const dateTime = `${date.getFullYear()}-${padStart(date.getMonth() + 1)}-${padStart(date.getDate())}_${padStart(date.getHours())}_${padStart(date.getMinutes())}`;
  const sanitizedTitle = (title || 'report')
    .replace(/[^a-zA-Z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return `${sanitizedTitle}_${dateTime}.pdf`;
};

/**
 * Download PDF from a given PDF link
 * @param {string} pdfLink - The PDF link to download
 * @param {string} filename - The filename to use for download
 * @param {function} setDownloadingState - Optional state setter for loading state
 * @returns {Promise<boolean>} Success status
 */
export const downloadPdf = async (pdfLink, filename, setDownloadingState = null) => {
  if (!pdfLink) {
    return false;
  }

  infoReactToastify(
    'Hang tight, we\'re downloading your pdf',
    { position: 'bottom-center', style: { backgroundColor: '#EEF8FF', width: '470px' } }
  );

  if (setDownloadingState) {
    setDownloadingState(true);
  }

  try {
    const response = await getPdfDownloadUrl(pdfLink);
    const downloadUrl = response.download_url;

    // Try to download directly first
    try {
      const fetchResponse = await fetch(downloadUrl);
      const blob = await fetchResponse.blob();

      // Create download link with blob
      // eslint-disable-next-line compat/compat
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      // eslint-disable-next-line compat/compat
      URL.revokeObjectURL(blobUrl);

      return true;
    }
    catch (fetchError) {
      // Fallback: open in new tab if direct download fails
      console.warn('Direct download failed, opening in new tab:', fetchError);
      window.open(downloadUrl, '_blank');

      return true;
    }
  }
  catch (error) {
    console.error('Error downloading PDF:', error);
    errorReactToastify(
      'Failed to download PDF. Please try again later.',
      { position: 'bottom-center', style: { backgroundColor: '#FDF0F0', width: '470px' } }
    );

    return false;
  }
  finally {
    if (setDownloadingState) {
      setDownloadingState(false);
    }
  }
};

/**
 * Download PDF for a review with automatic filename generation
 * @param {Object} review - The review object containing title and uuid
 * @param {string} pdfLink - The PDF link to download
 * @param {function} setDownloadingState - Optional state setter for loading state
 * @returns {Promise<boolean>} Success status
 */
export const downloadReviewPdf = async (review, pdfLink, setDownloadingState = null) => {
  const filename = generatePdfFilename(review?.title, new Date());

  return downloadPdf(pdfLink, filename, setDownloadingState);
};
